/**
 * State Machine Tool
 * 
 * Concrete implementation of IStateMachineTool interface.
 * This class wraps AdvancedStateManager and EntityManager to provide
 * a complete tool interface for state machine operations.
 */

import { AdvancedStateManager } from './AdvancedStateManager';
import { EntityManager, EntityType, EdgeType } from '../EntityManager';
import { IStateMachineTool } from '@/interfaces/stateManagement/StateMachineToolInterface';
import {
    StateCommand,
    StateTrigger,
    CommandAction,
    CommandExecutionResult,
    TriggerExecutionResult,
    ValidationResult,
} from '@/interfaces/stateManagement/CommandInterfaces';
import {
    SystemState,
    EntityWithDependencies,
    StateChangeEvent,
    CascadeDeleteResult,
} from '@/interfaces/stateManagement/StateManagerInterfaces';
import { DependencyCheckResult } from '@/interfaces/stateManagement/DependencyInterfaces';
import { ServiceInterface } from '@/interfaces/entities/entity_blueprints/ServiceInterface';
import { MiddlewareInterface } from '@/interfaces/entities/entity_blueprints/MiddlewareInterface';
import { EndPointInterface } from '@/interfaces/entities/entity_blueprints/EndPointInterface';
import { FunctionInterface } from '@/interfaces/entities/entity_blueprints/FunctionInterface';
import { MetaDataInterface } from '@/interfaces/entities/entity_blueprints/MetaDataInterface';
import { BackendModel } from '@/data/backendViewData';

/**
 * State Machine Tool
 * Concrete implementation of the state machine tool interface
 */
export class StateMachineTool implements IStateMachineTool {
    constructor(
        private stateManager: AdvancedStateManager,
        private entityManager: EntityManager
    ) {}

    // ==================== COMMAND EXECUTION ====================

    async executeCommand(command: StateCommand): Promise<CommandExecutionResult> {
        return await this.stateManager.executeCommand(command);
    }

    async executeTrigger(trigger: StateTrigger): Promise<TriggerExecutionResult> {
        return await this.stateManager.executeTrigger(trigger);
    }

    async validateCommand(command: StateCommand): Promise<ValidationResult> {
        return await this.stateManager.validateCommand(command);
    }

    // ==================== SERVICE OPERATIONS ====================

    async createService(service: ServiceInterface): Promise<CommandExecutionResult> {
        const command: StateCommand = {
            id: `cmd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            action: CommandAction.ADD_SERVICE,
            target: { service: service.id },
            data: { service },
            timestamp: new Date().toISOString(),
        };
        return await this.executeCommand(command);
    }

    async updateService(serviceId: string, updates: Partial<ServiceInterface>): Promise<CommandExecutionResult> {
        const command: StateCommand = {
            id: `cmd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            action: CommandAction.UPDATE_SERVICE,
            target: { service: serviceId },
            data: { updates },
            timestamp: new Date().toISOString(),
        };
        return await this.executeCommand(command);
    }

    async deleteService(serviceId: string, force: boolean = false): Promise<CommandExecutionResult> {
        const command: StateCommand = {
            id: `cmd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            action: CommandAction.REMOVE_SERVICE,
            target: { service: serviceId },
            timestamp: new Date().toISOString(),
        };
        // Force deletion through cascadeDelete if needed
        if (force) {
            const cascadeResult = await this.stateManager.cascadeDelete(serviceId, EntityType.SERVICE, force);
            return {
                commandId: command.id || 'unknown',
                success: cascadeResult.success,
                error: cascadeResult.errors.length > 0 ? cascadeResult.errors.join('; ') : undefined,
                affectedEntities: {
                    deleted: cascadeResult.deletedEntities,
                },
                edges: {
                    removed: cascadeResult.removedEdges,
                },
            };
        }
        return await this.executeCommand(command);
    }

    getService(serviceId: string): ServiceInterface | null {
        return this.entityManager.getService(serviceId) || null;
    }

    getServices(): ServiceInterface[] {
        return this.entityManager.getServices();
    }

    async addMethodToService(serviceId: string, functionObject: FunctionInterface): Promise<CommandExecutionResult> {
        const command: StateCommand = {
            id: `cmd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            action: CommandAction.ADD_METHOD,
            target: { service: serviceId, method: functionObject.id },
            functionObject,
            timestamp: new Date().toISOString(),
        };
        return await this.executeCommand(command);
    }

    async updateMethodInService(
        serviceId: string,
        methodId: string,
        updates: Partial<FunctionInterface>
    ): Promise<CommandExecutionResult> {
        const command: StateCommand = {
            id: `cmd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            action: CommandAction.UPDATE_METHOD,
            target: { service: serviceId, method: methodId },
            data: { updates },
            timestamp: new Date().toISOString(),
        };
        return await this.executeCommand(command);
    }

    async removeMethodFromService(serviceId: string, methodId: string): Promise<CommandExecutionResult> {
        const command: StateCommand = {
            id: `cmd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            action: CommandAction.REMOVE_METHOD,
            target: { service: serviceId, method: methodId },
            timestamp: new Date().toISOString(),
        };
        return await this.executeCommand(command);
    }

    // ==================== MIDDLEWARE OPERATIONS ====================

    async createMiddleware(middleware: MiddlewareInterface): Promise<CommandExecutionResult> {
        const command: StateCommand = {
            id: `cmd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            action: CommandAction.ADD_MIDDLEWARE,
            target: { middleware: middleware.id },
            data: { middleware },
            timestamp: new Date().toISOString(),
        };
        return await this.executeCommand(command);
    }

    async updateMiddleware(middlewareId: string, updates: Partial<MiddlewareInterface>): Promise<CommandExecutionResult> {
        const command: StateCommand = {
            id: `cmd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            action: CommandAction.UPDATE_MIDDLEWARE,
            target: { middleware: middlewareId },
            data: { updates },
            timestamp: new Date().toISOString(),
        };
        return await this.executeCommand(command);
    }

    async deleteMiddleware(middlewareId: string, force: boolean = false): Promise<CommandExecutionResult> {
        const command: StateCommand = {
            id: `cmd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            action: CommandAction.REMOVE_MIDDLEWARE,
            target: { middleware: middlewareId },
            timestamp: new Date().toISOString(),
        };
        if (force) {
            const cascadeResult = await this.stateManager.cascadeDelete(middlewareId, EntityType.MIDDLEWARE, force);
            return {
                commandId: command.id || 'unknown',
                success: cascadeResult.success,
                error: cascadeResult.errors.length > 0 ? cascadeResult.errors.join('; ') : undefined,
                affectedEntities: {
                    deleted: cascadeResult.deletedEntities,
                },
                edges: {
                    removed: cascadeResult.removedEdges,
                },
            };
        }
        return await this.executeCommand(command);
    }

    getMiddleware(middlewareId: string): MiddlewareInterface | null {
        return this.entityManager.getMiddleware(middlewareId) || null;
    }

    getMiddlewares(): MiddlewareInterface[] {
        return this.entityManager.getMiddlewares();
    }

    async attachFunctionToMiddleware(
        middlewareId: string,
        functionId: string,
        functionObject?: FunctionInterface
    ): Promise<CommandExecutionResult> {
        const command: StateCommand = {
            id: `cmd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            action: CommandAction.ATTACH_FUNCTION_TO_MIDDLEWARE,
            target: { middleware: middlewareId },
            data: { functionId },
            functionObject,
            timestamp: new Date().toISOString(),
        };
        return await this.executeCommand(command);
    }

    // ==================== ENDPOINT OPERATIONS ====================

    async createEndpoint(endpoint: EndPointInterface, functionObject?: FunctionInterface): Promise<CommandExecutionResult> {
        const command: StateCommand = {
            id: `cmd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            action: CommandAction.ADD_ENDPOINT,
            target: {
                endpoint: {
                    name: endpoint.id,
                    method: endpoint.method,
                    path: endpoint.path,
                },
            },
            data: { endpoint },
            functionObject,
            timestamp: new Date().toISOString(),
        };
        return await this.executeCommand(command);
    }

    async updateEndpoint(endpointId: string, updates: Partial<EndPointInterface>): Promise<CommandExecutionResult> {
        const command: StateCommand = {
            id: `cmd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            action: CommandAction.UPDATE_ENDPOINT,
            target: {
                endpoint: {
                    name: endpointId,
                },
            },
            data: { updates },
            timestamp: new Date().toISOString(),
        };
        return await this.executeCommand(command);
    }

    async deleteEndpoint(endpointId: string, force: boolean = false): Promise<CommandExecutionResult> {
        const command: StateCommand = {
            id: `cmd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            action: CommandAction.REMOVE_ENDPOINT,
            target: {
                endpoint: {
                    name: endpointId,
                },
            },
            timestamp: new Date().toISOString(),
        };
        if (force) {
            const cascadeResult = await this.stateManager.cascadeDelete(endpointId, EntityType.ENDPOINT, force);
            return {
                commandId: command.id || 'unknown',
                success: cascadeResult.success,
                error: cascadeResult.errors.length > 0 ? cascadeResult.errors.join('; ') : undefined,
                affectedEntities: {
                    deleted: cascadeResult.deletedEntities,
                },
                edges: {
                    removed: cascadeResult.removedEdges,
                },
            };
        }
        return await this.executeCommand(command);
    }

    getEndpoint(endpointId: string): EndPointInterface | null {
        return this.entityManager.getEndpoint(endpointId) || null;
    }

    getEndpoints(): EndPointInterface[] {
        return this.entityManager.getEndpoints();
    }

    async attachMiddlewareToEndpoint(endpointId: string, middlewareId: string): Promise<CommandExecutionResult> {
        const command: StateCommand = {
            id: `cmd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            action: CommandAction.ATTACH_MIDDLEWARE_TO_ENDPOINT,
            target: {
                endpoint: {
                    name: endpointId,
                },
            },
            data: { middlewareId },
            timestamp: new Date().toISOString(),
        };
        return await this.executeCommand(command);
    }

    async detachMiddlewareFromEndpoint(endpointId: string, middlewareId: string): Promise<CommandExecutionResult> {
        // Get current endpoint
        const endpoint = this.getEndpoint(endpointId);
        if (!endpoint) {
            return {
                commandId: `cmd_${Date.now()}`,
                success: false,
                error: `Endpoint ${endpointId} not found`,
                affectedEntities: { created: [], modified: [], deleted: [] },
                edges: { created: [], removed: [] },
            };
        }

        // Update endpoint to remove middleware
        const updatedMiddlewareIds = (endpoint.middlewareIds || []).filter(id => id !== middlewareId);
        return await this.updateEndpoint(endpointId, {
            middlewareIds: updatedMiddlewareIds,
        });
    }

    async attachFunctionToEndpoint(
        endpointId: string,
        functionId: string,
        functionObject?: FunctionInterface
    ): Promise<CommandExecutionResult> {
        const command: StateCommand = {
            id: `cmd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            action: CommandAction.ATTACH_FUNCTION_TO_ENDPOINT,
            target: {
                endpoint: {
                    name: endpointId,
                },
            },
            data: { functionId },
            functionObject,
            timestamp: new Date().toISOString(),
        };
        return await this.executeCommand(command);
    }

    async attachServiceToEndpoint(endpointId: string, serviceId: string): Promise<CommandExecutionResult> {
        const command: StateCommand = {
            id: `cmd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            action: CommandAction.ATTACH_SERVICE_TO_ENDPOINT,
            target: {
                endpoint: {
                    name: endpointId,
                },
            },
            data: { serviceId },
            timestamp: new Date().toISOString(),
        };
        return await this.executeCommand(command);
    }

    async updateEndpointModels(endpointId: string, modelIds: string[]): Promise<CommandExecutionResult> {
        return await this.updateEndpoint(endpointId, {
            modelsRequired: modelIds,
        });
    }

    // ==================== FUNCTION OPERATIONS ====================

    async createFunction(functionObject: FunctionInterface): Promise<CommandExecutionResult> {
        const command: StateCommand = {
            id: `cmd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            action: CommandAction.ADD_FUNCTION,
            target: { functionId: functionObject.id },
            functionObject,
            timestamp: new Date().toISOString(),
        };
        return await this.executeCommand(command);
    }

    async updateFunction(functionId: string, updates: Partial<FunctionInterface>): Promise<CommandExecutionResult> {
        const command: StateCommand = {
            id: `cmd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            action: CommandAction.UPDATE_FUNCTION,
            target: { functionId },
            data: { updates },
            timestamp: new Date().toISOString(),
        };
        return await this.executeCommand(command);
    }

    async deleteFunction(functionId: string, force: boolean = false): Promise<CommandExecutionResult> {
        const command: StateCommand = {
            id: `cmd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            action: CommandAction.REMOVE_FUNCTION,
            target: { functionId },
            timestamp: new Date().toISOString(),
        };
        return await this.executeCommand(command);
    }

    getFunction(functionId: string): FunctionInterface | null {
        return this.entityManager.getFunction(functionId) || null;
    }

    getFunctions(): FunctionInterface[] {
        return this.entityManager.getFunctions();
    }

    // ==================== MODEL OPERATIONS ====================

    async syncModel(model: BackendModel): Promise<CommandExecutionResult> {
        const command: StateCommand = {
            id: `cmd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            action: CommandAction.SYNC_MODEL,
            target: { model: model.id },
            data: { model },
            timestamp: new Date().toISOString(),
        };
        return await this.executeCommand(command);
    }

    async deleteModel(modelId: string, force: boolean = false): Promise<CommandExecutionResult> {
        const command: StateCommand = {
            id: `cmd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            action: CommandAction.REMOVE_MODEL,
            target: { model: modelId },
            timestamp: new Date().toISOString(),
        };
        if (force) {
            const cascadeResult = await this.stateManager.cascadeDelete(modelId, EntityType.MODEL, force);
            return {
                commandId: command.id || 'unknown',
                success: cascadeResult.success,
                error: cascadeResult.errors.length > 0 ? cascadeResult.errors.join('; ') : undefined,
                affectedEntities: {
                    deleted: cascadeResult.deletedEntities,
                },
                edges: {
                    removed: cascadeResult.removedEdges,
                },  
            };
        }
        return await this.executeCommand(command);
    }

    getModel(modelId: string): BackendModel | null {
        return this.entityManager.getModel(modelId) || null;
    }

    getModels(): BackendModel[] {
        return this.entityManager.getModels();
    }

    // ==================== DEPENDENCY OPERATIONS ====================

    async addDependency(
        sourceId: string,
        targetId: string,
        sourceType: EntityType,
        targetType: EntityType,
        edgeType: EdgeType,
        metadata?: Record<string, unknown>
    ): Promise<CommandExecutionResult> {
        const command: StateCommand = {
            id: `cmd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            action: CommandAction.ADD_DEPENDENCY,
            target: { service: sourceId }, // This is a placeholder, the actual logic is in data
            data: {
                sourceId,
                targetId,
                sourceType,
                targetType,
                edgeType,
                metadata,
            },
            timestamp: new Date().toISOString(),
        };
        return await this.executeCommand(command);
    }

    async removeDependency(sourceId: string, targetId: string, edgeType?: EdgeType): Promise<CommandExecutionResult> {
        const command: StateCommand = {
            id: `cmd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            action: CommandAction.REMOVE_DEPENDENCY,
            target: { service: sourceId }, // Placeholder
            data: {
                sourceId,
                targetId,
                edgeType,
            },
            timestamp: new Date().toISOString(),
        };
        return await this.executeCommand(command);
    }

    checkDependencies(entityId: string, entityType: EntityType): DependencyCheckResult {
        return this.stateManager.checkDependencies(entityId, entityType);
    }

    getEntityWithDependencies(entityId: string, entityType: EntityType): EntityWithDependencies | null {
        return this.stateManager.getEntityWithDependencies(entityId, entityType);
    }

    canDeleteEntity(entityId: string, entityType: EntityType): boolean {
        const entityWithDeps = this.getEntityWithDependencies(entityId, entityType);
        return entityWithDeps?.canDelete ?? false;
    }

    getBlockingDependencies(
        entityId: string,
        entityType: EntityType
    ): Array<{ entityId: string; entityType: EntityType; relationship: string }> {
        const dependencyCheck = this.checkDependencies(entityId, entityType);
        return dependencyCheck.blockingDependencies || [];
    }

    async cascadeDelete(entityId: string, entityType: EntityType, force: boolean = false): Promise<CascadeDeleteResult> {
        return await this.stateManager.cascadeDelete(entityId, entityType, force);
    }

    // ==================== SYSTEM STATE OPERATIONS ====================

    getSystemState(): SystemState {
        return this.stateManager.getSystemState();
    }

    resetState(metadata?: MetaDataInterface): void {
        this.stateManager.resetState(metadata);
    }

    setMetadata(metadata?: MetaDataInterface): void {
        this.stateManager.setMetadata(metadata);
    }

    getMetadata(): MetaDataInterface  {
        return this.stateManager.getSystemState().metadata || {} as MetaDataInterface;
    }

    // ==================== STATIC DEPENDENCY OPERATIONS ====================

    applyStaticDependencies(entity: unknown, entityType: EntityType): void {
        this.stateManager.applyStaticDependencies(entity, entityType);
    }

    // ==================== EVENT SUBSCRIPTION ====================

    subscribe(callback: (event: StateChangeEvent) => void): () => void {
        return this.stateManager.subscribe(callback);
    }

    getRecentEvents(limit: number = 50): StateChangeEvent[] {
        // Note: AdvancedStateManager doesn't currently track recent events
        // This would need to be implemented by storing events in the manager
        // For now, return empty array
        return [];
    }

    // ==================== EDGE OPERATIONS ====================

    getEdgesForEntity(entityId: string): Array<{
        id: string;
        sourceId: string;
        targetId: string;
        edgeType: EdgeType;
        metadata?: Record<string, unknown>;
    }> {
        const edges = this.entityManager.getEdgesForEntity(entityId);
        return edges.map(edge => ({
            id: edge.id,
            sourceId: edge.sourceId,
            targetId: edge.targetId,
            edgeType: edge.edgeType,
            metadata: edge.metadata,
        }));
    }

    getAllEdges(): Array<{
        id: string;
        sourceId: string;
        targetId: string;
        sourceType: EntityType;
        targetType: EntityType;
        edgeType: EdgeType;
        metadata?: Record<string, unknown>;
    }> {
        const edges = this.entityManager.getAllEdges();
        return edges.map(edge => ({
            id: edge.id,
            sourceId: edge.sourceId,
            targetId: edge.targetId,
            sourceType: edge.sourceType,
            targetType: edge.targetType,
            edgeType: edge.edgeType,
            metadata: edge.metadata,
        }));
    }

    findEdge(
        sourceId: string,
        targetId: string,
        edgeType?: EdgeType
    ): { id: string; sourceId: string; targetId: string; edgeType: EdgeType; metadata?: Record<string, unknown> } | undefined {
        const edge = this.entityManager.findEdge(sourceId, targetId, edgeType);
        if (!edge) return undefined;
        return {
            id: edge.id,
            sourceId: edge.sourceId,
            targetId: edge.targetId,
            edgeType: edge.edgeType,
            metadata: edge.metadata,
        };
    }

    // ==================== QUERY OPERATIONS ====================

    getEntitiesByType(entityType: EntityType): unknown[] {
        switch (entityType) {
            case EntityType.SERVICE:
                return this.getServices();
            case EntityType.MIDDLEWARE:
                return this.getMiddlewares();
            case EntityType.ENDPOINT:
                return this.getEndpoints();
            case EntityType.FUNCTION:
                return this.getFunctions();
            case EntityType.MODEL:
                return this.getModels();
            default:
                return [];
        }
    }

    getEntity(entityId: string, entityType: EntityType): unknown | null {
        switch (entityType) {
            case EntityType.SERVICE:
                return this.getService(entityId);
            case EntityType.MIDDLEWARE:
                return this.getMiddleware(entityId);
            case EntityType.ENDPOINT:
                return this.getEndpoint(entityId);
            case EntityType.FUNCTION:
                return this.getFunction(entityId);
            case EntityType.MODEL:
                return this.getModel(entityId);
            default:
                return null;
        }
    }

    entityExists(entityId: string, entityType: EntityType): boolean {
        return this.getEntity(entityId, entityType) !== null;
    }

    // ==================== VALIDATION OPERATIONS ====================

    validateEntity(entity: unknown, entityType: EntityType): ValidationResult {
        // This would need to be implemented by checking entity structure
        // For now, create a basic validation
        // Note: EntityManager has validation methods but they're private
        // We'll need to access them or implement validation here
        return {
            valid: true,
            errors: [],
            warnings: [],
        };
    }

    async canExecuteCommand(command: StateCommand): Promise<boolean> {
        const validation = await this.validateCommand(command);
        return validation.valid;
    }

    // ==================== UTILITY OPERATIONS ====================

    getStats(): {
        services: number;
        middlewares: number;
        endpoints: number;
        functions: number;
        models: number;
        edges: number;
    } {
        return this.entityManager.getStats();
    }

    getEntityCounts(): Record<EntityType, number> {
        return {
            [EntityType.SERVICE]: this.getServices().length,
            [EntityType.MIDDLEWARE]: this.getMiddlewares().length,
            [EntityType.ENDPOINT]: this.getEndpoints().length,
            [EntityType.FUNCTION]: this.getFunctions().length,
            [EntityType.MODEL]: this.getModels().length,
        };
    }

    clear(): void {
        this.resetState();
    }
}

